import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import FusionInputField from 'components/FusionInputField/FusionInputField';
import useWindow from 'hooks/useWindow';
import { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { TbListSearch } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useGetAuditLogsMetaQuery } from 'services/auditLogs_v2/auditLogs_v2.api';
import { DEFAULT_FILTERS } from '../AuditLogs_v2.index';
import { AuditLogFilters, tryToISODateString } from '../utils/searchBuilder';

interface Props {
  committedFilters: AuditLogFilters;
  isFeatchingLogs: boolean;
  onSearch: (draft: AuditLogFilters) => void;
}

export default function AuditLogFiltersBar({ committedFilters, isFeatchingLogs, onSearch }: Props) {
  // draft holds in-progress text/date input values — does NOT trigger a query until Search is clicked
  const [draft, setDraft] = useState<AuditLogFilters>(committedFilters);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const { width, height } = useWindow();

  const setDraftField = (patch: Partial<AuditLogFilters>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const handleDateFieldChange = (field: 'dateFrom' | 'dateTo', rawValue: string) => {
    if (!rawValue) {
      setDraftField({ [field]: undefined } as Partial<AuditLogFilters>);
      return;
    }

    const { value, error } = tryToISODateString(rawValue);
    if (error) {
      toast.error('invalid date format, must be YYYY-MM-DD');
      setDraftField({ [field]: undefined } as Partial<AuditLogFilters>);
      return;
    }

    setDraftField({ [field]: value } as Partial<AuditLogFilters>);
  };

  const { data: meta } = useGetAuditLogsMetaQuery();

  const domainOptions = useMemo(
    () => Object.keys(meta?.domains ?? {}).map((d) => ({ label: d, value: d })),
    [meta],
  );

  const categoryOptions = useMemo(
    () => (meta?.categories ?? []).map((c) => ({ label: c, value: c })),
    [meta],
  );

  // // Action options are scoped to the currently selected domain, or empty when no domain is chosen
  // const actionOptions = useMemo(() => {
  //   if (!draft.domain || !meta?.domains[draft.domain]) return [];
  //   return meta.domains[draft.domain].map((a) => ({ label: a, value: a }));
  // }, [draft.domain, meta]);

  const ORDER_BY_OPTIONS: { value: AuditLogFilters['orderBy']; label: string }[] = [
    { value: 'action', label: 'Action' },
    { value: 'category', label: 'Category' },
    { value: 'created_at', label: 'Date' },
    { value: 'domain', label: 'Domain' },
    { value: 'user_id', label: 'User' },
  ];

  const ORDER_DIR_OPTIONS: { value: AuditLogFilters['orderDir']; label: string }[] = [
    { value: 'DESC', label: 'Newest first' },
    { value: 'ASC', label: 'Oldest first' },
  ];

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((size) => ({
    value: size,
    label: String(size),
  }));

  // '' is the "All" row; the filter itself is undefined when nothing is picked.
  const ALL_OPTION = { value: '', label: 'All' };
  const categorySelectOptions = [ALL_OPTION, ...categoryOptions];
  const domainSelectOptions = [ALL_OPTION, ...domainOptions];

  return (
    <div
      className="d-flex flex-wrap justify-content-center align-items-end gap-2"
      style={{ width: 'auto', maxWidth: '100%' }}
    >
      {/* Search and Advanced Search Button in one row */}
      <div
        className="d-flex flex-row align-items-end gap-2 justify-content-end"
        style={{ minWidth: 120 }}
      >
        {/* Custom Search Bar */}
        {!advancedSearchOpen && (
          <div
            className="d-flex align-items-center bg-primary rounded-3 border border-1 border-secondary px-2 w-100"
            style={{ height: 40, minWidth: 0 }}
          >
            <input
              type="text"
              className="form-control border-0 bg-transparent text-white px-0 w-100"
              style={{ boxShadow: 'none' }}
              placeholder="Search Audit Logs..."
              value={draft.search ?? ''}
              onChange={(e) => setDraftField({ search: e.target.value || undefined })}
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  onSearch({ ...draft, search: draft.search?.trim() || undefined });
              }}
            />
            <Button
              className="bg-transparent border-0 px-2"
              style={{ color: 'white', height: 36 }}
              onClick={() => onSearch({ ...draft, search: draft.search?.trim() || undefined })}
              title="Search"
              disabled={isFeatchingLogs}
            >
              {isFeatchingLogs ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <FaMagnifyingGlass size={18} />
              )}
            </Button>
          </div>
        )}
        {/* Advanced Search Toggle as Button */}
        {/* Determine if any advanced filters are selected */}
        {(() => {
          // Compare all relevant fields to DEFAULT_FILTERS
          const hasFilters =
            (draft.category !== undefined && draft.category !== null) ||
            (draft.domain !== undefined && draft.domain !== null) ||
            (draft.dateFrom !== undefined && draft.dateFrom !== null) ||
            (draft.dateTo !== undefined && draft.dateTo !== null) ||
            draft.orderBy !== DEFAULT_FILTERS.orderBy ||
            draft.orderDir !== DEFAULT_FILTERS.orderDir ||
            draft.pageSize !== DEFAULT_FILTERS.pageSize;
          return (
            <div className="d-flex flex-row position-relative">
              {/* Advanced Search Banner */}
              {advancedSearchOpen && (
                <div
                  className="position-absolute top-00 end-100 bg-dark bg-opacity-75 border border-secondary border-2 rounded shadow p-3 z-3"
                  style={{
                    marginRight: 8,
                    minWidth: 260,
                    maxWidth: 600,
                    width: 'auto',
                  }}
                >
                  <div
                    className="d-flex align-items-center bg-primary rounded-3 border border-1 border-secondary px-2 w-100"
                    style={{ height: 40, minWidth: 0 }}
                  >
                    {/* Search Bar */}
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent text-white px-0 w-100"
                      style={{ boxShadow: 'none' }}
                      placeholder="Search Audit Logs..."
                      value={draft.search ?? ''}
                      onChange={(e) => setDraftField({ search: e.target.value || undefined })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter')
                          onSearch({ ...draft, search: draft.search?.trim() || undefined });
                      }}
                    />
                    <Button
                      className="bg-transparent border-0 px-2"
                      style={{ color: 'white', height: 36 }}
                      onClick={() =>
                        onSearch({ ...draft, search: draft.search?.trim() || undefined })
                      }
                      title="Search"
                      disabled={isFeatchingLogs}
                    >
                      {isFeatchingLogs ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        <FaMagnifyingGlass size={18} />
                      )}
                    </Button>
                  </div>

                  {/* Category */}
                  <BaseSelect
                    wrapperClassName="flex-grow-1 min-width-0"
                    label="Category"
                    isSearchable
                    selectProps={{
                      options: categorySelectOptions,
                      value: findSelectOption(categorySelectOptions, draft.category ?? ''),
                      onChange: (option: { value: string } | null) =>
                        setDraftField({ category: option?.value || undefined }),
                    }}
                  />

                  {/* Domain */}
                  <BaseSelect
                    wrapperClassName="flex-grow-1 min-width-0"
                    label="Domain"
                    isSearchable
                    selectProps={{
                      options: domainSelectOptions,
                      value: findSelectOption(domainSelectOptions, draft.domain ?? ''),
                      onChange: (option: { value: string } | null) =>
                        setDraft((prev) => ({ ...prev, domain: option?.value || undefined })),
                    }}
                  />

                  {/* Date From */}
                  <Form.Group className="flex-grow-1 min-width-0" controlId="audit-log-date-from">
                    <Form.Label className="fs-6">From</Form.Label>
                    <Form.Control
                      type="date"
                      className="bg-primary rounded border border-1 border-secondary text-white"
                      style={{ colorScheme: 'dark' }}
                      value={draft.dateFrom ?? ''}
                      onChange={(e) => handleDateFieldChange('dateFrom', e.target.value)}
                    />
                  </Form.Group>

                  {/* Date To */}
                  <Form.Group className="flex-grow-1 min-width-0" controlId="audit-log-date-to">
                    <Form.Label className="fs-6">To</Form.Label>
                    <Form.Control
                      type="date"
                      className="bg-primary rounded border border-1 border-secondary text-white"
                      style={{ colorScheme: 'dark' }}
                      value={draft.dateTo ?? ''}
                      onChange={(e) => handleDateFieldChange('dateTo', e.target.value)}
                    />
                  </Form.Group>

                  {/* Order By */}
                  <BaseSelect
                    wrapperClassName="flex-grow-1 min-width-0"
                    label="Order By"
                    isSearchable
                    selectProps={{
                      options: ORDER_BY_OPTIONS,
                      value: findSelectOption(ORDER_BY_OPTIONS, draft.orderBy ?? 'created_at'),
                      onChange: (option: { value: AuditLogFilters['orderBy'] } | null) => {
                        if (option) setDraftField({ orderBy: option.value });
                      },
                    }}
                  />

                  {/* Order Direction */}
                  <BaseSelect
                    wrapperClassName="flex-grow-1 min-width-0"
                    label="Direction"
                    isSearchable={false}
                    selectProps={{
                      options: ORDER_DIR_OPTIONS,
                      value: findSelectOption(ORDER_DIR_OPTIONS, draft.orderDir ?? 'DESC'),
                      onChange: (option: { value: AuditLogFilters['orderDir'] } | null) => {
                        if (option) setDraftField({ orderDir: option.value });
                      },
                    }}
                  />

                  {/* Page Size */}
                  <BaseSelect
                    wrapperClassName="flex-grow-1"
                    label="Per Page"
                    isSearchable={false}
                    selectProps={{
                      options: PAGE_SIZE_OPTIONS,
                      value: findSelectOption(PAGE_SIZE_OPTIONS, draft.pageSize ?? 10),
                      onChange: (option: { value: number } | null) => {
                        if (option) setDraftField({ pageSize: option.value });
                      },
                    }}
                  />
                </div>
              )}

              {/* Advanced Search Button */}
              <Button
                type="button"
                variant={hasFilters ? 'primary' : 'secondary'}
                className="position-relative end-0 d-flex align-items-center justify-content-center px-2"
                style={{ minWidth: 40, maxWidth: 48, height: 40 }}
                onClick={() => setAdvancedSearchOpen((open) => !open)}
                title="Advanced Search"
              >
                <TbListSearch size={22} color="#fff" />
              </Button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
